import {useEffect, useCallback, useRef} from 'react';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useAppSelector, useAppDispatch, store} from 'store/customer';
import {
    getHistoryStack,
    getIsBusy,
    setHistoryStackActiveIndex,
    setHistoryItemRecentlyRedone,
    setHistoryItemRecentlyUndone,
    setIsBusy,
    setIsRoomPlannerEditing,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    useDeleteCabinetMutation,
    invalidateParticularRoomAndCost,
} from 'components/customer/Product/store/productApi';
import {useJobContext} from 'contexts';
import {PartialJob} from 'shared/types/PartialJob';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {dispatchClearChanges} from 'store/customer/appSlice';
import {useSearchParams} from 'react-router-dom';
import {
    undoPositionChange,
    redoPositionChange,
} from 'components/customer/RoomPlanner/helpers/history/positionChangeAction';
import {
    undoCabinetAdd,
    redoCabinetAdd,
} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import {
    undoCabinetDelete,
    redoCabinetDelete,
} from 'components/customer/RoomPlanner/helpers/history/cabinetDeleteAction';
import {
    undoCabinetUpdate,
    redoCabinetUpdate,
} from 'components/customer/RoomPlanner/helpers/history/cabinetUpdateAction';
import {
    undoPlaceholderAdd,
    redoPlaceholderAdd,
    undoPlaceholderDelete,
    redoPlaceholderDelete,
    undoPlaceholderUpdate,
    redoPlaceholderUpdate,
} from 'components/customer/RoomPlanner/helpers/history/placeholderHistory';
import {HistoryStack} from 'components/customer/RoomPlanner/types';
import {
    findLastActiveIndex,
    findSupportedActionIndex,
} from 'components/customer/RoomPlanner/components/helpers/UndoRedo';

const useUndoRedo = () => {
    const {
        setUndoHandler,
        setRedoHandler,
        setGoToHistoryIndex,
        undoHandler,
        redoHandler,
        cabinetGroupListRef,
        deletedCabinetsRef,
        setSelectedCabinetGroup,
        placeholderListRef,
    } = useRoomPlannerContext();
    const historyStack = useAppSelector(getHistoryStack);
    const isBusy = useAppSelector(getIsBusy);
    const historyStackRef = useRef(historyStack);
    historyStackRef.current = historyStack;
    const dispatch = useAppDispatch();
    const [deleteProduct] = useDeleteCabinetMutation();
    const {room, updateTotalProductCountManual} = useJobContext() as PartialJob;
    const [searchParams, setSearchParams] = useSearchParams();
    const isRedoingLastItemRef = useRef(false);

    const deselectEditingCabinet = useCallback(
        (id: number, type: number) => {
            const params = new URLSearchParams(searchParams);
            let paramsChanged = false;

            if (
                params.has('cabinetId') &&
                params.get('cabinetId') === id.toString()
            ) {
                params.delete('cabinetId');
                paramsChanged = true;
            }

            if (
                params.has('product') &&
                params.get('product') === type.toString()
            ) {
                params.delete('product');
                paramsChanged = true;
            }

            if (paramsChanged) {
                setSearchParams(params);
                dispatch(setIsRoomPlannerEditing(false));
            }
        },
        [searchParams, setSearchParams, dispatch]
    );

    const invalidateCaches = useCallback(() => {
        dispatch(invalidateTotalProductCount());
        dispatch(invalidateParticularRoomAndCost(Number(room.id)));
        dispatchClearChanges();
        setSelectedCabinetGroup?.(null);
    }, [dispatch, room.id, setSelectedCabinetGroup]);

    // Helper function to safely set the next history index with bounds checking.
    // Returns a Promise when it triggers redoHandler so callers can await and not proceed until redo completes.
    const setNextHistoryIndex = useCallback(
        async (
            currentIndex: number,
            targetIndexFromJump?: number
        ): Promise<void> => {
            if (isBusy) return;
            const nextIndex = currentIndex + 1;
            const shouldIncrementNextIndex =
                historyStack[Number(nextIndex)]?.recentlyUndone === true;
            const firstItemRedone =
                nextIndex === 1 && historyStack[0].recentlyUndone;
            const fromManuallyRedoing =
                typeof targetIndexFromJump === 'undefined';

            if (nextIndex < historyStack.length) {
                dispatch(setHistoryStackActiveIndex(nextIndex));
            } else {
                dispatch(setHistoryStackActiveIndex(currentIndex));
            }

            const executeLastItem =
                nextIndex === historyStack.length - 1 &&
                historyStack[Number(nextIndex)].recentlyUndone;

            if (
                shouldIncrementNextIndex ||
                (executeLastItem && (!firstItemRedone || !fromManuallyRedoing))
            ) {
                isRedoingLastItemRef.current = true;
                const result = redoHandler?.(true) as unknown;
                if (
                    result != null &&
                    typeof (result as {then?: unknown}).then === 'function'
                ) {
                    await (result as Promise<unknown>);
                }
            }
        },
        [dispatch, historyStack, redoHandler, isBusy]
    );

    // Helper function to safely set the previous history index with bounds checking
    const setPreviousHistoryIndex = useCallback(
        (currentIndex: number) => {
            const previousIndex = currentIndex - 1;
            if (previousIndex >= 0) {
                dispatch(setHistoryStackActiveIndex(previousIndex));
            } else {
                // Stay at current index if previous index would be out of bounds
                dispatch(setHistoryStackActiveIndex(currentIndex));
            }
        },
        [dispatch]
    );

    // Helper function to find the target history item for undo/redo.
    // Accepts stack so handlers can pass latest from store.getState() and avoid stale closure.
    const findTargetHistoryItem = useCallback(
        (
            stack: HistoryStack[],
            direction: 'undo' | 'redo'
        ): {
            targetIndex: number;
            historyItem: HistoryStack[][0];
            isCabinetAction: boolean;
        } | null => {
            if (!cabinetGroupListRef?.current || stack.length === 0) {
                return null;
            }

            const {index: currentActiveIndex, isCabinetAction} =
                findLastActiveIndex(stack);

            if (currentActiveIndex === -1) {
                return null;
            }

            const targetIndex = findSupportedActionIndex(
                stack,
                currentActiveIndex,
                direction
            );

            if (targetIndex < 0 || targetIndex >= stack.length) {
                return null;
            }

            return {
                targetIndex,
                historyItem: stack[Number(targetIndex)],
                isCabinetAction,
            };
        },
        [cabinetGroupListRef]
    );

    const actionContext = useCallback(
        () => ({
            dispatch,
            cabinetGroupListRef,
            placeholderListRef,
            deletedCabinetsRef,
            setNextHistoryIndex,
            setPreviousHistoryIndex,
            deleteProduct,
            deselectEditingCabinet,
            invalidateCaches,
            updateTotalProductCountManual,
            historyStack,
            isBusy,
        }),
        [
            dispatch,
            cabinetGroupListRef,
            placeholderListRef,
            deletedCabinetsRef,
            setNextHistoryIndex,
            setPreviousHistoryIndex,
            deleteProduct,
            deselectEditingCabinet,
            invalidateCaches,
            updateTotalProductCountManual,
            historyStack,
            isBusy,
        ]
    );

    useEffect(() => {
        setUndoHandler(async () => {
            if (isBusy) return;
            // Read latest state so rapid Undo sees updated active index and doesn't re-run same entry
            const stack = getHistoryStack(store.getState());
            const result = findTargetHistoryItem(stack, 'undo');
            if (!result) return;

            const {targetIndex, historyItem} = result;
            const ctx = actionContext();

            if (historyItem.action === 'CABINET_UPDATE') {
                dispatch(setIsBusy(true));
            }
            if (historyItem.action === 'POSITION_CHANGE') {
                undoPositionChange(historyItem, targetIndex, ctx);
            } else if (historyItem.action === 'CABINET_ADD') {
                if (historyItem.data?.isPlaceholder) {
                    undoPlaceholderAdd(historyItem, targetIndex, ctx);
                } else {
                    await undoCabinetAdd(historyItem, targetIndex, ctx);
                }
            } else if (historyItem.action === 'CABINET_DELETE') {
                if (historyItem.data?.isPlaceholder) {
                    await undoPlaceholderDelete(historyItem, targetIndex, ctx);
                } else {
                    undoCabinetDelete(historyItem, targetIndex, ctx);
                }
            } else if (historyItem.action === 'CABINET_UPDATE') {
                if (historyItem.data?.isPlaceholder) {
                    await undoPlaceholderUpdate(historyItem, targetIndex, ctx);
                } else {
                    await undoCabinetUpdate(historyItem, targetIndex, ctx);
                }
            }

            dispatch(
                setHistoryItemRecentlyUndone({
                    index: targetIndex,
                    recentlyUndone: true,
                })
            );

            dispatch(
                setHistoryItemRecentlyRedone({
                    index: targetIndex,
                    recentlyRedone: false,
                })
            );

            if (historyItem.action === 'CABINET_UPDATE') {
                dispatch(setIsBusy(false));
            }
        });

        setRedoHandler(
            async (
                skipHistoryIndexUpdate?: boolean,
                targetIndexParam?: number
            ) => {
                if (isBusy && !isRedoingLastItemRef.current) {
                    return;
                }
                isRedoingLastItemRef.current = false;

                const stack = getHistoryStack(store.getState());
                const result = findTargetHistoryItem(stack, 'redo');
                if (!result) return;

                const {targetIndex, historyItem} = result;
                const baseCtx = actionContext();
                // If requested, skip advancing the history index by
                // providing a no-op setNextHistoryIndex.
                const ctx = skipHistoryIndexUpdate
                    ? {
                          ...baseCtx,
                          setNextHistoryIndex: () => {
                              /* no-op: skip advancing history index */
                          },
                          targetIndex: targetIndexParam,
                      }
                    : {...baseCtx, targetIndex: targetIndexParam};

                if (historyItem.action === 'CABINET_UPDATE') {
                    dispatch(setIsBusy(true));
                }

                const shouldSkipAction =
                    historyItem.recentlyRedone &&
                    !historyItem.recentlyUndone &&
                    targetIndex === 0;

                if (shouldSkipAction) {
                    await ctx.setNextHistoryIndex(
                        targetIndex,
                        targetIndexParam
                    );
                } else {
                    if (historyItem.action === 'POSITION_CHANGE') {
                        await redoPositionChange(historyItem, targetIndex, ctx);
                    } else if (historyItem.action === 'CABINET_ADD') {
                        if (historyItem.data?.isPlaceholder) {
                            await redoPlaceholderAdd(
                                historyItem,
                                targetIndex,
                                ctx
                            );
                        } else {
                            await redoCabinetAdd(historyItem, targetIndex, ctx);
                        }
                    } else if (historyItem.action === 'CABINET_DELETE') {
                        if (historyItem.data?.isPlaceholder) {
                            redoPlaceholderDelete(
                                historyItem,
                                targetIndex,
                                ctx
                            );
                        } else {
                            await redoCabinetDelete(
                                historyItem,
                                targetIndex,
                                ctx
                            );
                        }
                    } else if (historyItem.action === 'CABINET_UPDATE') {
                        if (historyItem.data?.isPlaceholder) {
                            await redoPlaceholderUpdate(
                                historyItem,
                                targetIndex,
                                ctx
                            );
                        } else {
                            await redoCabinetUpdate(
                                historyItem,
                                targetIndex,
                                ctx
                            );
                        }
                    }
                }

                // Clear recentlyUndone when redoing
                dispatch(
                    setHistoryItemRecentlyUndone({
                        index: targetIndex,
                        recentlyUndone: false,
                    })
                );

                dispatch(
                    setHistoryItemRecentlyRedone({
                        index: targetIndex,
                        recentlyRedone: true,
                    })
                );

                if (historyItem.action === 'CABINET_UPDATE') {
                    dispatch(setIsBusy(false));
                }
            }
        );

        // Handler to go to a specific history index by repeating undo/redo
        if (setGoToHistoryIndex) {
            const getCurrentActiveIndex = () => {
                const stack = historyStackRef.current;
                if (!stack?.length) return -1;
                for (let i = stack.length - 1; i >= 0; i--) {
                    if (stack[Number(i)].isActive) return i;
                }
                return -1;
            };

            const MAX_GO_TO_ATTEMPTS = 100;
            const goTo = async (targetIndex: number, attempt = 0) => {
                if (attempt >= MAX_GO_TO_ATTEMPTS) return;
                const current = getCurrentActiveIndex();

                if (
                    current === -1 ||
                    targetIndex < 0 ||
                    targetIndex >= historyStackRef.current.length
                ) {
                    return;
                }

                if (current === targetIndex) return;

                const result =
                    targetIndex < current
                        ? undoHandler?.()
                        : redoHandler?.(false, targetIndex);
                const resultUnknown = result as unknown;
                if (
                    resultUnknown != null &&
                    typeof (resultUnknown as {then?: unknown}).then ===
                        'function'
                ) {
                    await (resultUnknown as Promise<unknown>);
                }

                await new Promise((resolve) => setTimeout(resolve, 175));
                void goTo(targetIndex, attempt + 1);
            };
            setGoToHistoryIndex((index: number) => void goTo(index, 0));
        }
    }, [
        setUndoHandler,
        setRedoHandler,
        setGoToHistoryIndex,
        undoHandler,
        redoHandler,
        historyStack,
        actionContext,
        findTargetHistoryItem,
        isBusy,
    ]);
};

export default useUndoRedo;
